import { createElement, type ReactElement, type ReactNode } from "react";

import ReactDOM from "react-dom";
import { FormattedMessage } from "react-intl";

import { PDF_CONTAINER_ID, PDF_ELEMENT_ID } from "./TaxInvoices.consts";
import type { InvoiceEnvWeb } from "./TaxInvoices.types";

/** The PDF page is hidden on screen, so show it in the copy being captured. */
const revealPdfPage = (clonedDocument: Document) => {
  for (const id of Object.values(PDF_ELEMENT_ID)) {
    const element = clonedDocument.getElementById(id);
    if (element) {
      element.style.display = "block";
    }
  }
};

/** html2canvas and jsPDF load from a script tag, so they live on `window`. */
type PdfGlobals = {
  html2canvas: (
    element: HTMLElement,
    options?: {
      windowWidth?: number;
      scale?: number;
      x?: number;
      allowTaint?: boolean;
      useCORS?: boolean;
      onclone?: (clonedDocument: Document) => void;
    },
  ) => Promise<HTMLCanvasElement>;

  jsPDF: new (
    orientation?: string,
    unit?: string,
    format?: string | number[],
  ) => {
    addPage: () => void;
    addImage: (
      imageData: string,
      format: string,
      x: number,
      y: number,
      width: number,
      height: number,
    ) => void;
    save: (filename: string) => void;
  };
};

const getPdfGlobals = () => window as unknown as PdfGlobals;

/** Renders a node off-screen, captures it, and saves it as a multi-page PDF. */
const downloadPdf = async (
  children: ReactElement,
  name: string,
  onClone: (clonedDocument: Document) => void,
) => {
  const SharedIntlProviderHOC = (
    await import("shared-components/hocs/sharedIntlProviderHOC")
  ).default;

  const container = document.getElementById(PDF_CONTAINER_ID);

  if (!container) {
    return;
  }

  // `render` must commit synchronously so the capture below sees the markup.
  // eslint-disable-next-line react/no-deprecated
  ReactDOM.render(
    createElement(SharedIntlProviderHOC, null, children),
    container,
  );

  const { html2canvas, jsPDF } = getPdfGlobals();

  const pdfPage = document.getElementById(PDF_ELEMENT_ID.page);

  if (!pdfPage) {
    return;
  }

  const canvas = await html2canvas(pdfPage, {
    windowWidth: 1000,
    scale: 2,
    x: 0,
    allowTaint: true,
    useCORS: true,
    onclone: onClone,
  });

  const image = {
    type: "jpeg",
    quality: 0.98,
  };
  const margin = [0.5, 0.5];
  const imgWidth = 8.5;
  let pageHeight = 11;

  const innerPageWidth = imgWidth - margin[0] * 2;
  const innerPageHeight = pageHeight - margin[1] * 2;

  // Split the capture into page-sized slices.
  const pxFullHeight = canvas.height;
  const pxPageHeight = Math.floor(canvas.width * (pageHeight / imgWidth));
  const nPages = Math.ceil(pxFullHeight / pxPageHeight);

  pageHeight = innerPageHeight;

  const pageCanvas = document.createElement("canvas");
  const pageCtx = pageCanvas.getContext("2d");
  pageCanvas.width = canvas.width;
  pageCanvas.height = pxPageHeight;

  if (!pageCtx) {
    return;
  }

  const doc = new jsPDF("p", "in", [8.5, 11]);
  for (let page = 0; page < nPages; page++) {
    // Trim the last page to its content.
    if (page === nPages - 1 && pxFullHeight % pxPageHeight !== 0) {
      pageCanvas.height = pxFullHeight % pxPageHeight;
      pageHeight = (pageCanvas.height * innerPageWidth) / pageCanvas.width;
    }

    const w = pageCanvas.width;
    const h = pageCanvas.height;
    pageCtx.fillStyle = "white";
    pageCtx.fillRect(0, 0, w, h);
    pageCtx.drawImage(canvas, 0, page * pxPageHeight, w, h, 0, 0, w, h);

    if (page > 0) doc.addPage();
    const imgData = pageCanvas.toDataURL("image/" + image.type, image.quality);
    doc.addImage(
      imgData,
      image.type,
      margin[1],
      margin[0],
      innerPageWidth,
      pageHeight,
    );
  }
  doc.save(name);

  // eslint-disable-next-line react/no-deprecated
  ReactDOM.unmountComponentAtNode(container);
};

type TaxDetails = {
  taxNumberName: ReactNode | null;
  taxNumber: string | null;
  contactPhoneNumber?: string | null;
};

/** Peerspace's own tax registration and contact details for a country. */
const getTaxDetails = (
  country: string | undefined,
  envWeb: InvoiceEnvWeb | undefined,
): TaxDetails => {
  if (!envWeb || !country) {
    return {
      taxNumberName: null,
      taxNumber: null,
    };
  }

  const TAX_NUMBER_NAME_BY_COUNTRY: Record<string, ReactNode> = {
    US: createElement(FormattedMessage, {
      id: "hosting.sales_tax_invoice.fein",
      defaultMessage: "FEIN",
    }),
    AU: createElement(FormattedMessage, {
      id: "hosting.invoice_page.abn",
      defaultMessage: "ABN",
    }),
  };

  const TAX_NUMBER_ENV_KEY_BY_COUNTRY: Record<string, string> = {
    US: "formatted_registration_id",
    AU: "formatted_abn_id",
  };

  const CONTACT_PHONE_NUMBER_BY_COUNTRY: Record<string, string> = {
    US: "(415) 403-2421",
  };

  const taxNumberName = TAX_NUMBER_NAME_BY_COUNTRY[country] ?? null;
  const taxNumberEvnKey = TAX_NUMBER_ENV_KEY_BY_COUNTRY[country] ?? null;
  const contactPhoneNumber = CONTACT_PHONE_NUMBER_BY_COUNTRY[country] ?? null;

  const taxNumber = taxNumberEvnKey
    ? (envWeb?.ps_addresses?.[country]?.[taxNumberEvnKey] ?? null)
    : null;

  return {
    taxNumberName,
    taxNumber,
    contactPhoneNumber,
  };
};

export { downloadPdf, getTaxDetails, revealPdfPage };
export type { TaxDetails };
